<template>
  <!-- Contact Information -->
  <div class="w-100">
    <job-request-section-heading class="text-bold HEADER">
      Sign Up Information
      <vk-icon
          v-tooltip="{
            content:infoContent,
          }"
          name="information-filled"
          size="20"/>
    </job-request-section-heading>

    <form>
      <!-- Customer Email Password -->
      <div class="layout gap-2">
        <!-- Customer Email -->
        <div class="layout-item is-12 is-6:t">
          <div
              :class="{ 'input-message error': shouldShowEmailError }"
              :error="emailError"
              selenium="account-error"
              class="input-container">
            <input
                v-vk-input
                v-model.trim="email"
                :disabled="disabled || isLoggedIn"
                class="input"
                label="Email"
                type="email"
                name="email"
                autocomplete="email"
                selenium="email-input">
            <label class="input-label">Email</label>
          </div>
        </div>
      </div>
      <div class="layout gap-2 m-y-2">
        <!-- Customer Password -->
        <client-only>
          <div v-if="!isLoggedIn" class="layout-item is-12 is-6:t">
            <div class="layout-item input-container">
              <password-field
                  refName="passwordForm"
                  :class="{ 'error': validation.hasError('password') }"
                  :error="validation.firstError('password')"
                  v-model.trim="password"/>
            </div>
          </div>
        </client-only>
        <client-only>
          <div v-if="!isLoggedIn" class="layout-item is-12 is-6:t">
            <div class="layout-item input-container">
              <password-field
                  refName="passwordFormConfirmation"
                  label="Confirm Password"
                  :class="{ 'error': shouldShowPasswordConfirmationError }"
                  :error="passwordConfirmationError"
                  v-model.trim="passwordConfirmation"
                  @focusChange="onFocus"/>
            </div>
          </div>
        </client-only>
      </div>
      <div class="layout gap-2 m-x-1 m-b-1">
        <a
            v-if="!isLoggedIn"
            class="link text-underline"
            type="button"
            @click="$emit('sign-in-clicked')">
          Click here to sign in if you have an account.
        </a>
      </div>
      <!-- Customer Name -->
      <job-request-section-heading class="text-bold">
        Contact information
      </job-request-section-heading>
      <div class="layout gap-2 m-y-2">
        <!-- First Name -->
        <div class="layout-item is-12 is-6:t">
          <div
              :class="{ 'input-message error': validation.hasError('firstName') }"
              :error="validation.firstError('firstName')"
              class="layout-item input-container">
            <input
                v-vk-input
                v-model.trim="firstName"
                :disabled="disabled"
                class="input"
                label="First Name"
                name="fname"
                autocomplete="given-name"
                autocapitalize="sentences"
                selenium="first-name-input">
            <label class="input-label">First Name</label>
          </div>
        </div>

        <!-- Last Name -->
        <div class="layout-item is-12 is-6:t">
          <div
              :class="{ 'input-message error': validation.hasError('lastName') }"
              :error="validation.firstError('lastName')"
              class="layout-item input-container">
            <input
                v-vk-input
                v-model.trim="lastName"
                :disabled="disabled"
                class="input"
                label="Last Name"
                name="lname"
                autocomplete="family-name"
                autocapitalize="sentences"
                selenium="last-name-input">
            <label class="input-label">Last Name</label>
          </div>
        </div>
      </div>

      <!-- Customer Phone -->
      <div class="layout gap-2">
        <div class="layout-item is-12 is-6:t">
          <div
              :class="{ 'input-message error': validation.hasError('phone') }"
              :error="validation.firstError('phone')"
              class="input-container">
            <vk-phone-input
                v-model="phone"
                :disabled="disabled"
                class="input"
                selenium="phone-input"/>
            <label class="input-label">Phone</label>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import JobRequestSectionHeading from '@/views/JobRequest/components/JobRequestSectionHeading';
import PasswordField from '@/components/PasswordField';

export default {
  name: 'request-form',
  components: {
    JobRequestSectionHeading,
    PasswordField,
  },
  props: {
    validation: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    userExists: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      focused: false,
      shouldShowPasswordConfirmationError: false,
      infoContent: `Creating an account allows you to request service and receive updates on your requests.<br><br>
        If you have already submitted a request with Pro Referral, you may already have an account. Click on Sign In and use your email and password, or one-time-code, to access your existing account.`,
    };
  },
  computed: {
    ...mapGetters('user', [
      'isLoggedIn',
    ]),
    shouldShowEmailError () {
      return this.validation.hasError('email') || (this.userExists && !this.isLoggedIn);
    },
    emailError () {
      const firstError = this.validation.firstError('email');
      const userExistsError = 'An account has already been created with this email. Please sign in.';

      if (firstError) {
        return firstError;
      }

      if (this.userExists) {
        return userExistsError;
      }

      return '';
    },
    passwordConfirmationError () {
      const firstError = this.validation.firstError('passwordConfirmation');
      if (firstError) {
        return firstError;
      }
      if (
        this.passwordConfirmation // Only check if user has typed something
    && this.password !== this.passwordConfirmation
      ) {
        return 'Password and password confirmation do not match.';
      }
      return '';
    },
    email: {
      get () {
        return this.$store.getters['jobrequest/overview/email'];
      },
      set (value) {
        this.$store.dispatch('jobrequest/overview/setEmail', value);
      },
    },
    password: {
      get () {
        return this.$store.getters['jobrequest/overview/password'];
      },
      set (value) {
        this.$store.dispatch('jobrequest/overview/setPassword', value);
      },
    },
    passwordConfirmation: {
      get () {
        return this.$store.getters['jobrequest/overview/passwordConfirmation'];
      },
      set (value) {
        this.$store.dispatch('jobrequest/overview/setPasswordConfirmation', value);
      },
    },
    firstName: {
      get () {
        return this.$store.getters['jobrequest/overview/firstName'];
      },
      set (value) {
        this.$store.dispatch('jobrequest/overview/setFirstName', value);
      },
    },
    lastName: {
      get () {
        return this.$store.getters['jobrequest/overview/lastName'];
      },
      set (value) {
        this.$store.dispatch('jobrequest/overview/setLastName', value);
      },
    },
    phone: {
      get () {
        return this.$store.getters['jobrequest/overview/phone'];
      },
      set (value) {
        this.$store.dispatch('jobrequest/overview/setPhone', value);
      },
    },
  },
  methods: {
    onFocus ($event) {
      this.focused = $event;
      if (!this.focused) {
        this.shouldShowPasswordConfirmationError = this.validation.hasError('passwordConfirmation') || this.password !== this.passwordConfirmation;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools.scss";

  .HEADER {
    svg{
      color: $color-depot;
    }
  }
</style>
